import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Switch,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { useCookieConsent } from "../../../privacy/CookieConsentContext";

export default function CookieConsent() {
  const classes = useStyle();
  const {
    consent,
    preferences,
    preferencesOpen,
    openPreferences,
    closePreferences,
    savePreferences,
  } = useCookieConsent();
  const [draft, setDraft] = useState(preferences);

  useEffect(() => {
    if (preferencesOpen) setDraft(preferences);
  }, [preferences, preferencesOpen]);

  const acceptAll = () =>
    savePreferences({ analytics: true, externalMedia: true });
  const rejectOptional = () =>
    savePreferences({ analytics: false, externalMedia: false });

  return (
    <>
      {!consent && !preferencesOpen && (
        <section
          aria-label="Cookie consent"
          aria-live="polite"
          className={classes.banner}
        >
          <div className={classes.bannerCopy}>
            <Typography variant="h2">Your privacy choices</Typography>
            <Typography variant="body1">
              We use optional analytics to understand site use and optional
              external-media technologies to display YouTube videos. They remain
              disabled unless you allow them. You can change your choice at any
              time. <RouterLink to="/cookie-policy">Cookie Policy</RouterLink>
            </Typography>
          </div>
          <div className={classes.bannerActions}>
            <Button variant="outlined" onClick={rejectOptional}>
              Reject optional
            </Button>
            <Button variant="outlined" onClick={openPreferences}>
              Customize
            </Button>
            <Button variant="outlined" onClick={acceptAll}>
              Accept all
            </Button>
          </div>
        </section>
      )}

      <Dialog
        aria-labelledby="cookie-preferences-title"
        fullWidth
        maxWidth="sm"
        open={preferencesOpen}
        onClose={consent ? closePreferences : undefined}
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle id="cookie-preferences-title">
          Cookie preferences
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" className={classes.dialogIntro}>
            Choose which optional technologies may run. Essential storage is
            limited to remembering these choices and supporting requested
            account functions. See the full{" "}
            <Link component={RouterLink} to="/cookie-policy">
              Cookie Policy
            </Link>
            .
          </Typography>
          <div className={classes.preferenceRow}>
            <div>
              <Typography variant="h3">Essential</Typography>
              <Typography variant="body2">
                Required for privacy choices and requested site functions.
              </Typography>
            </div>
            <Switch checked disabled inputProps={{ "aria-label": "Essential" }} />
          </div>
          <div className={classes.preferenceRow}>
            <div>
              <Typography variant="h3">Analytics</Typography>
              <Typography variant="body2">
                Allows Google Analytics to measure visits and page use.
              </Typography>
            </div>
            <FormControlLabel
              className={classes.switchLabel}
              control={
                <Switch
                  checked={draft.analytics}
                  color="primary"
                  inputProps={{ "aria-label": "Analytics" }}
                  onChange={(event) =>
                    setDraft({ ...draft, analytics: event.target.checked })
                  }
                />
              }
              label=""
            />
          </div>
          <div className={classes.preferenceRow}>
            <div>
              <Typography variant="h3">External media</Typography>
              <Typography variant="body2">
                Allows YouTube videos to load from Google services.
              </Typography>
            </div>
            <FormControlLabel
              className={classes.switchLabel}
              control={
                <Switch
                  checked={draft.externalMedia}
                  color="primary"
                  inputProps={{ "aria-label": "External media" }}
                  onChange={(event) =>
                    setDraft({ ...draft, externalMedia: event.target.checked })
                  }
                />
              }
              label=""
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button variant="outlined" onClick={rejectOptional}>
            Reject optional
          </Button>
          <Button variant="outlined" onClick={() => savePreferences(draft)}>
            Save choices
          </Button>
          <Button variant="outlined" onClick={acceptAll}>
            Accept all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const useStyle = makeStyles(({ breakpoints, palette }) => ({
  banner: {
    position: "fixed",
    zIndex: 10000,
    left: "2rem",
    right: "2rem",
    bottom: "2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2rem",
    padding: "2rem",
    color: "white",
    background: "#101b2c",
    border: "1px solid rgba(255, 255, 255, 0.35)",
    borderRadius: "0.6rem",
    boxShadow: "0 1rem 4rem rgba(0, 0, 0, 0.5)",
    "& a": {
      color: palette.primary.light,
    },
    [breakpoints.down("sm")]: {
      left: "1rem",
      right: "1rem",
      bottom: "1rem",
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  bannerCopy: {
    maxWidth: "76rem",
    "& h2": {
      marginBottom: "0.8rem",
      fontSize: "2rem",
      fontFamily: "Akira",
      lineHeight: 1.2,
    },
    "& p": {
      fontSize: "1.3rem",
      lineHeight: 1.5,
    },
  },
  bannerActions: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(13rem, 1fr))",
    gap: "1rem",
    "& button": {
      minHeight: "4.4rem",
      color: "white",
      borderColor: "rgba(255, 255, 255, 0.65)",
      fontSize: "1.1rem",
      fontWeight: 600,
    },
    [breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  dialog: {
    color: "white",
    background: "#101b2c",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "0.6rem",
    "& h2": {
      fontSize: "2.2rem",
      fontFamily: "Akira",
    },
    "& a": {
      color: palette.primary.light,
    },
  },
  dialogIntro: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    lineHeight: 1.5,
  },
  preferenceRow: {
    minHeight: "8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2rem",
    padding: "1.5rem 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.16)",
    "& h3": {
      marginBottom: "0.5rem",
      fontSize: "1.5rem",
    },
    "& p": {
      fontSize: "1.2rem",
      lineHeight: 1.4,
    },
  },
  switchLabel: {
    margin: 0,
  },
  dialogActions: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "1rem",
    padding: "1.5rem 2.4rem",
    "& button": {
      minHeight: "4.4rem",
      margin: 0,
      color: "white",
      borderColor: "rgba(255, 255, 255, 0.65)",
      fontSize: "1.1rem",
      fontWeight: 600,
    },
    [breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
}));
